import type { ApiSchemas } from '@rental/contracts/client'

type StaffCash = ApiSchemas['StaffCash']
export type CashDrawer = StaffCash['drawers'][number]

/**
 * S5 (PAY-6): where a money form's cash goes. The device remembers its
 * pick-up point; with one point there is nothing to choose. A drawer being
 * counted takes no cash until its close is finished.
 */
export type DrawerState =
  /** No pick-up point is in service: nowhere for cash to go. */
  | { kind: 'none' }
  /** Several points and none chosen on this device yet. */
  | { kind: 'choose' }
  | { kind: 'closed'; drawer: CashDrawer }
  | { kind: 'counting'; drawer: CashDrawer }
  | { kind: 'open'; drawer: CashDrawer; sessionId: string }

export function drawerState(cash: StaffCash, chosenPoint: string | null): DrawerState {
  const [only] = cash.drawers
  if (!only) return { kind: 'none' }
  const drawer =
    cash.drawers.find((item) => item.location.id === chosenPoint) ??
    (cash.drawers.length === 1 ? only : undefined)
  if (!drawer) return { kind: 'choose' }
  const session = drawer.session
  if (!session) return { kind: 'closed', drawer }
  if (session.firstCountCents !== null) return { kind: 'counting', drawer }
  return { kind: 'open', drawer, sessionId: session.id }
}

/** A drawer as the Cassa screen shows it: closed, open, or counted and waiting for its close. */
export type DrawerKind = 'closed' | 'open' | 'counting'

/**
 * What a press on this device just did to a drawer (UI-2g). Said from the
 * press's own answer, so a list read again that does not come never leaves a
 * press that worked unsaid, and a second press refused for what the first
 * already did. Said until the drawer moves on from where the press left it:
 * once the list shows it there (`seen`), the next change, by anyone, ends it,
 * and it is never said again for a change this device did not make.
 */
export interface DrawerOutcome {
  what: 'opened' | 'openedElsewhere' | 'counted' | 'closed'
  at: number
  seen: boolean
}

/** Where each press leaves the drawer. */
export const LEFT_AT: Readonly<Record<DrawerOutcome['what'], DrawerKind>> = {
  opened: 'open',
  openedElsewhere: 'open',
  counted: 'counting',
  closed: 'closed',
}

/** The outcome as the list now shows the drawer: kept as it is, seen there, or over. */
export function settleOutcome(
  outcome: DrawerOutcome | null,
  kind: DrawerKind,
): DrawerOutcome | null {
  if (!outcome) return null
  if (kind === LEFT_AT[outcome.what]) return outcome.seen ? outcome : { ...outcome, seen: true }
  return outcome.seen ? null : outcome
}

/**
 * The close, once the blind count is in: what the drawer should hold is
 * known, so a count that differs needs a reason before it closes.
 */
export function closeNeedsReason(countCents: number, expectedCents: number): boolean {
  return countCents !== expectedCents
}

/** An opening count below the cash deposits whose notes are in the drawer is short. */
export function openingShort(countCents: number, depositsHeldCents: number): boolean {
  return countCents < depositsHeldCents
}

const POINT_KEY = 'rental.staff.cashPoint'

/** This device's pick-up point, as it was last chosen here (Fady, 15 Oct: a drawer per point). */
export function rememberedPoint(): string | null {
  try {
    return window.localStorage.getItem(POINT_KEY)
  } catch {
    // Storage can be refused (private mode): the point is asked for again.
    return null
  }
}

export function rememberPoint(locationId: string): void {
  try {
    window.localStorage.setItem(POINT_KEY, locationId)
  } catch {
    // Not remembered across reloads; the choice still holds for now.
  }
}
